import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Search, Plus, Calendar, Users, DollarSign, Home,
  Trash2, X, ChevronDown, Filter, ArrowUp, ArrowDown, Upload, FileText, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';

const API_URL = window.location.origin;

const Bookings = () => {
  const userId = useUserId();
  
  const [bookings, setBookings] = useState([]);
  const [bookingStats, setBookingStats] = useState({});
  const [bookingStatusFilter, setBookingStatusFilter] = useState('Overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAllotModal, setShowAllotModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [roomNumber, setRoomNumber] = useState('');
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    if (!userId) return;
    try {
      let url = `${API_URL}/api/orders/bookings?user_id=${userId}`;
      if (bookingStatusFilter !== 'Overview') {
        url += `&status=${bookingStatusFilter}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const res = await fetch(url);
      const data = await res.json();
      setBookings(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  }, [userId, bookingStatusFilter, searchQuery]);

  const fetchBookingStats = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/bookings/stats?user_id=${userId}`);
      const data = await res.json();
      setBookingStats(data);
    } catch (error) {
      console.error('Error fetching booking stats:', error);
    }
  }, [userId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBookings(), fetchBookingStats()]);
      setLoading(false);
    };
    
    if (userId) {
      loadData();
    }
  }, [userId, fetchBookings, fetchBookingStats]);

  // Delete booking
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/bookings/${bookingId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('Booking deleted');
        fetchBookings();
        fetchBookingStats();
      }
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  // Allot room
  const handleAllotRoom = async () => {
    if (!selectedBooking || !roomNumber) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/bookings/${selectedBooking.id}/allot-room?room_number=${roomNumber}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Room ${roomNumber} allotted`);
        setShowAllotModal(false);
        setSelectedBooking(null);
        setRoomNumber('');
        fetchBookings();
      }
    } catch (error) {
      toast.error('Failed to allot room');
    }
  };

  // File upload handling
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'text/csv'];
    const isCSV = file.name.endsWith('.csv');
    
    if (!allowedTypes.includes(file.type) && !isCSV) {
      toast.error('Unsupported file type. Please upload PDF, CSV, JPG, PNG, or WebP files.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploadProgress('reading');
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        setUploadProgress('parsing');
        
        try {
          const response = await fetch(`${API_URL}/api/orders/parse-document`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              file_base64: base64,
              file_type: isCSV ? 'text/csv' : file.type,
              file_name: file.name,
              user_id: userId,
              target_type: 'bookings'
            })
          });

          const result = await response.json();
          
          if (result.success && result.bookings?.length > 0) {
            setParsedData(result.bookings);
            setUploadProgress(null);
            toast.success(`Found ${result.bookings.length} bookings in document`);
          } else {
            setUploadProgress(null);
            toast.error(result.message || 'No bookings found in document');
          }
        } catch (error) {
          setUploadProgress(null);
          toast.error('Failed to parse document');
          console.error('Parse error:', error);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadProgress(null);
      toast.error('Failed to read file');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportParsed = async () => {
    if (!parsedData?.length) return;
    
    setUploadProgress('importing');
    
    try {
      const response = await fetch(`${API_URL}/api/orders/import-parsed?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orders: [],
          bookings: parsedData
        })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Imported ${result.imported_bookings} bookings`);
        setParsedData(null);
        setShowUploadModal(false);
        fetchBookings();
        fetchBookingStats();
      } else {
        toast.error('Failed to import bookings');
      }
    } catch (error) {
      toast.error('Failed to import bookings');
    }
    
    setUploadProgress(null);
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-500 text-white';
      case 'Pending': return 'bg-yellow-500 text-black';
      case 'Billed': return 'bg-blue-500 text-white';
      case 'Partially Paid': return 'bg-orange-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getRoomTypeColor = (type) => {
    switch (type) {
      case 'Premium Room': return 'bg-purple-600';
      case 'Deluxe Suite': return 'bg-blue-600';
      case 'Presidential Suite': return 'bg-amber-600';
      case 'Twin Room': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading bookings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Bookings</h1>
            <p className="text-gray-400 mt-1">Manage room bookings and guest reservations</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors"
              data-testid="upload-bookings-btn"
            >
              <Upload size={20} />
              Upload
            </button>
            
            <button
              onClick={() => setShowNewBooking(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              data-testid="new-booking-btn"
            >
              <Plus size={20} />
              New Booking
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Arrival (this week)</span>
              <div className={`flex items-center gap-1 text-xs ${
                (bookingStats.arrivals_this_week || 0) > (bookingStats.arrivals_last_week || 0) ? 'text-green-400' : 'text-red-400'
              }`}>
                {(bookingStats.arrivals_this_week || 0) > (bookingStats.arrivals_last_week || 0) ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                {Math.abs(((bookingStats.arrivals_this_week || 0) - (bookingStats.arrivals_last_week || 0)) / Math.max(bookingStats.arrivals_last_week || 1, 1) * 100).toFixed(0)}%
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{bookingStats.arrivals_this_week || 0}</p>
            <p className="text-xs text-gray-500">vs {bookingStats.arrivals_last_week || 0} last week</p>
          </div>

          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Departure (this week)</span>
              <div className={`flex items-center gap-1 text-xs ${
                (bookingStats.departures_this_week || 0) > (bookingStats.departures_last_week || 0) ? 'text-green-400' : 'text-red-400'
              }`}>
                {(bookingStats.departures_this_week || 0) > (bookingStats.departures_last_week || 0) ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                {Math.abs(((bookingStats.departures_this_week || 0) - (bookingStats.departures_last_week || 0)) / Math.max(bookingStats.departures_last_week || 1, 1) * 100).toFixed(0)}%
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{bookingStats.departures_this_week || 0}</p>
            <p className="text-xs text-gray-500">vs {bookingStats.departures_last_week || 0} last week</p>
          </div>

          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Booking (this week)</span>
              <div className={`flex items-center gap-1 text-xs ${
                (bookingStats.bookings_this_week || 0) > (bookingStats.bookings_last_week || 0) ? 'text-green-400' : 'text-red-400'
              }`}>
                {(bookingStats.bookings_this_week || 0) > (bookingStats.bookings_last_week || 0) ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                {Math.abs(((bookingStats.bookings_this_week || 0) - (bookingStats.bookings_last_week || 0)) / Math.max(bookingStats.bookings_last_week || 1, 1) * 100).toFixed(0)}%
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{bookingStats.bookings_this_week || 0}</p>
            <p className="text-xs text-gray-500">vs {bookingStats.bookings_last_week || 0} last week</p>
          </div>

          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <span className="text-gray-400 text-sm block mb-2">Today Activities</span>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <Home size={14} className="text-green-400" />
                <span className="text-gray-400">Available:</span>
                <span className="text-white">{bookingStats.available_rooms || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-blue-400" />
                <span className="text-gray-400">Booked:</span>
                <span className="text-white">{bookingStats.booked_rooms || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users size={14} className="text-purple-400" />
                <span className="text-gray-400">Guests:</span>
                <span className="text-white">{bookingStats.total_guests || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign size={14} className="text-yellow-400" />
                <span className="text-gray-400">Revenue:</span>
                <span className="text-white">${(bookingStats.total_revenue || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Status Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['Overview', 'Confirmed', 'Active', 'Waitlist', 'Cancelled', 'Refunded'].map(status => (
            <button
              key={status}
              onClick={() => setBookingStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                bookingStatusFilter === status 
                  ? 'bg-white text-black' 
                  : 'bg-black text-gray-300 hover:bg-gray-800 border border-gray-700'
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ID or Guest name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-black border border-gray-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-600"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-3 bg-black border border-gray-400 rounded-lg text-gray-300 hover:bg-gray-800">
            <Filter size={18} />
            Status
            <ChevronDown size={16} />
          </button>
        </div>

        {/* Bookings Table */}
        <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-600">
                <th className="text-left p-4 text-xs font-medium text-gray-400">Booking ID</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400">Guest Name</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400">Room Type</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400">Nights</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400">Check In - Check Out</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400">Source</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400">Amount Status</th>
                <th className="text-left p-4 text-xs font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-400">
                    <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No bookings found</p>
                  </td>
                </tr>
              ) : (
                bookings.map(booking => (
                  <tr key={booking.id} className="border-b border-gray-700 hover:bg-gray-900/50">
                    <td className="p-4 text-white font-medium">{booking.booking_id}</td>
                    <td className="p-4 text-white">{booking.guest_name}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs text-white ${getRoomTypeColor(booking.room_type)}`}>
                        {booking.room_type}
                      </span>
                    </td>
                    <td className="p-4 text-white">{booking.nights}</td>
                    <td className="p-4 text-gray-300 text-sm">
                      {booking.check_in} - {booking.check_out}
                    </td>
                    <td className="p-4 text-gray-300">{booking.source}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">${booking.amount}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getPaymentStatusColor(booking.payment_status)}`}>
                          {booking.payment_status}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowAllotModal(true);
                          }}
                          className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs flex items-center gap-1"
                        >
                          <Plus size={12} />
                          Allot Room
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded">
                          <Calendar size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteBooking(booking.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Allot Room Modal */}
        {showAllotModal && selectedBooking && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Allot Room</h2>
                <button onClick={() => { setShowAllotModal(false); setSelectedBooking(null); setRoomNumber(''); }} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="bg-black rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Guest</p>
                  <p className="text-white font-medium">{selectedBooking.guest_name}</p>
                </div>
                <div className="bg-black rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Room Type</p>
                  <p className="text-white font-medium">{selectedBooking.room_type}</p>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Room Number</label>
                  <input
                    type="text"
                    value={roomNumber}
                    onChange={(e) => setRoomNumber(e.target.value)}
                    placeholder="e.g., 101, 202"
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleAllotRoom}
                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
                  >
                    Allot Room
                  </button>
                  <button
                    onClick={() => { setShowAllotModal(false); setSelectedBooking(null); setRoomNumber(''); }}
                    className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* New Booking Modal */}
        {showNewBooking && (
          <NewBookingModal
            userId={userId}
            onClose={() => setShowNewBooking(false)}
            onCreated={() => {
              setShowNewBooking(false);
              fetchBookings();
              fetchBookingStats();
            }}
          />
        )}
        
        {/* Upload Modal */}
        {showUploadModal && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowUploadModal(false); setParsedData(null); setUploadProgress(null); }}
            data-testid="upload-bookings-modal"
          >
            <div 
              className="bg-black border border-gray-400 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                <h3 className="text-white text-lg font-semibold">Upload Bookings</h3>
                <button 
                  onClick={() => { setShowUploadModal(false); setParsedData(null); setUploadProgress(null); }} 
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="px-6 py-5 space-y-5">
                {/* Upload Area */}
                {!parsedData && (
                  <div className="space-y-4">
                    <p className="text-gray-400 text-sm">
                      Upload a PDF, CSV, or image file containing booking data. The system will automatically extract reservation information.
                    </p>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.csv,.jpg,.jpeg,.png,.webp"
                      className="hidden"
                      data-testid="booking-file-input"
                    />
                    
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-gray-500 transition-colors"
                    >
                      {uploadProgress ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <p className="text-white">
                            {uploadProgress === 'reading' ? 'Reading file...' : 
                             uploadProgress === 'parsing' ? 'Extracting bookings with AI...' : 
                             'Processing...'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <Upload size={40} className="mx-auto text-gray-500 mb-3" />
                          <p className="text-white font-medium">Click to upload or drag and drop</p>
                          <p className="text-gray-500 text-sm mt-1">PDF, CSV, JPG, PNG, WebP (max 10MB)</p>
                        </>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <FileText size={16} />
                        <span>CSV reservation lists</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Image size={16} />
                        <span>Screenshots of bookings</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Parsed Data Preview */}
                {parsedData && parsedData.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-medium">Found {parsedData.length} Bookings</h4>
                      <button
                        onClick={() => { setParsedData(null); }}
                        className="text-gray-400 hover:text-white text-sm"
                      >
                        Upload different file
                      </button>
                    </div>
                    
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {parsedData.map((booking, idx) => (
                        <div key={idx} className="bg-black border border-gray-700 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-white font-medium">{booking.guest_name || 'Unknown Guest'}</p>
                              <p className="text-gray-400 text-sm">{booking.room_type} • {booking.nights} night(s)</p>
                              <p className="text-gray-500 text-xs">{booking.check_in} → {booking.check_out}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-medium">${(booking.amount || 0).toLocaleString()}</p>
                              <p className="text-gray-500 text-xs">{booking.source}</p>
                            </div>
                          </div>
                          {(booking.guest_email || booking.guest_phone) && (
                            <div className="mt-2 pt-2 border-t border-gray-800 text-gray-400 text-sm">
                              {booking.guest_email && <p>{booking.guest_email}</p>}
                              {booking.guest_phone && <p>{booking.guest_phone}</p>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
                      <button
                        onClick={() => { setShowUploadModal(false); setParsedData(null); }}
                        className="px-4 py-2 text-gray-300 hover:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleImportParsed}
                        disabled={uploadProgress === 'importing'}
                        className="px-5 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
                        data-testid="import-bookings-btn"
                      >
                        {uploadProgress === 'importing' ? 'Importing...' : `Import ${parsedData.length} Bookings`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// New Booking Modal — creates a booking via POST /api/orders/bookings
const NewBookingModal = ({ userId, onClose, onCreated }) => {
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [roomType, setRoomType] = useState('Premium Room');
  const [roomNumber, setRoomNumberField] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [source, setSource] = useState('Direct');
  const [amount, setAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('Pending');
  const [status, setStatus] = useState('Confirmed');
  const [specialRequests, setSpecialRequests] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Compute nights from check-in / check-out
  const computeNights = () => {
    if (!checkIn || !checkOut) return 0;
    const diff = (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24);
    return diff > 0 ? Math.round(diff) : 0;
  };
  const nights = computeNights();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!guestName.trim()) { toast.error('Guest name is required'); return; }
    if (!checkIn || !checkOut) { toast.error('Check-in and check-out dates are required'); return; }
    if (nights <= 0) { toast.error('Check-out must be after check-in'); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 0) { toast.error('Enter a valid amount'); return; }
    
    setSubmitting(true);
    try {
      const body = {
        guest_name: guestName.trim(),
        guest_email: guestEmail.trim() || null,
        guest_phone: guestPhone.trim() || null,
        room_type: roomType,
        room_number: roomNumber.trim() || null,
        nights,
        check_in: checkIn,
        check_out: checkOut,
        source,
        amount: amt,
        payment_status: paymentStatus,
        status,
        special_requests: specialRequests.trim() || null,
        user_id: userId,
      };
      const res = await fetch(`${API_URL}/api/orders/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Booking ${data.booking_id} created`);
        onCreated();
      } else {
        toast.error(data.detail || 'Failed to create booking');
      }
    } catch (err) {
      toast.error('Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="new-booking-modal"
    >
      <div 
        className="bg-black border border-gray-400 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-black z-10">
          <h3 className="text-white text-lg font-semibold">New Booking</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-new-booking">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Guest */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Guest Name *</label>
              <input
                type="text"
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                placeholder="e.g. Jane Doe"
                required
                data-testid="guest-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Guest Email</label>
              <input
                type="email"
                value={guestEmail}
                onChange={e => setGuestEmail(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                placeholder="guest@example.com"
                data-testid="guest-email-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Guest Phone</label>
              <input
                type="tel"
                value={guestPhone}
                onChange={e => setGuestPhone(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                placeholder="+1 555 123 4567"
                data-testid="guest-phone-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Source</label>
              <select
                value={source}
                onChange={e => setSource(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                data-testid="source-select"
              >
                <option>Direct</option>
                <option>Agoda</option>
                <option>Booking.com</option>
                <option>MakeMyTrip</option>
                <option>Goibibo</option>
                <option>Expedia</option>
                <option>Airbnb</option>
                <option>Other</option>
              </select>
            </div>
          </div>

          {/* Room */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Room Type *</label>
              <select
                value={roomType}
                onChange={e => setRoomType(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                data-testid="room-type-select"
              >
                <option>Premium Room</option>
                <option>Deluxe Suite</option>
                <option>Presidential Suite</option>
                <option>Twin Room</option>
                <option>Single Room</option>
                <option>Family Room</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Room Number</label>
              <input
                type="text"
                value={roomNumber}
                onChange={e => setRoomNumberField(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                placeholder="e.g. 204"
                data-testid="room-number-input"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Check-in *</label>
              <input
                type="date"
                value={checkIn}
                onChange={e => setCheckIn(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                required
                data-testid="checkin-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Check-out *</label>
              <input
                type="date"
                value={checkOut}
                onChange={e => setCheckOut(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                required
                data-testid="checkout-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Nights</label>
              <div className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm">
                {nights || '—'}
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Amount *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                placeholder="0.00"
                required
                data-testid="amount-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Payment Status</label>
              <select
                value={paymentStatus}
                onChange={e => setPaymentStatus(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                data-testid="payment-select"
              >
                <option>Pending</option>
                <option>Paid</option>
                <option>Partially Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                data-testid="status-select"
              >
                <option>Confirmed</option>
                <option>Active</option>
                <option>Waitlist</option>
                <option>Cancelled</option>
                <option>Refunded</option>
              </select>
            </div>
          </div>

          {/* Special requests */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Special Requests</label>
            <textarea
              value={specialRequests}
              onChange={e => setSpecialRequests(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm resize-none"
              placeholder="Late check-in, dietary needs, etc."
              data-testid="special-requests-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white"
              data-testid="cancel-new-booking"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-new-booking"
            >
              {submitting ? 'Creating…' : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Bookings;
